import { ApiToken } from "@/shared/api/token/api-token";
import type { ApiErrorResponse } from "@/shared/api/types";
import { useEffect, useMemo, useState } from "react";
import { decodeWbToken, type DecodedToken } from "@/shared/lib/decodeToken";
import styles from "./TokenModal.module.scss";
import type { AxiosError } from "axios";
import { type Person } from "@/shared/hooks/usePeople";
import { DropdownIcon } from "./assets/DropdownIcon";
import instructionImg from "./assets/instruction.png";
import { useToastStore } from "@/shared/stores/toastStore";
import { openExternalLink } from "@/shared/lib/openExternalLink";
import {
  ADD_CABINET_WEB_URL,
  grantedRoleMessage,
  readAccessRequiresInvite,
  tokenAddErrorMessage,
  type AccessRequiresInvite,
} from "./model/token-add-outcome";

interface TokenModalProps {
  isOpen: boolean;
  onClose: () => void;
  suppliers: Person[];
  refetchSuppliers: () => void;
  onTokenAdded: ((supplierId: string) => void) | null;
}

interface Permission {
  name: string;
  hasAccess: boolean;
}

interface TokenInfo {
  token: string;
  personId: string;
  personName: string;
  phone: string;
  status: string;
  balance: string;
  autoRenewal: boolean;
  permissions: {
    analytics: boolean;
    reviews: boolean;
    promotion: boolean;
    content: boolean;
    statistics: boolean;
    marketplace: boolean;
    finance: boolean;
  };
}

export function TokenModal({
  isOpen,
  onClose,
  suppliers,
  refetchSuppliers,
  onTokenAdded,
}: TokenModalProps) {
  const [token, setToken] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [decodedToken, setDecodedToken] = useState<DecodedToken | null>(null);
  const [validationError, setValidationError] = useState("");
  const [isClosing, setIsClosing] = useState(false);
  const [isInstructionOpen, setInstructionOpen] = useState(false);
  const [allowedScopes, setAllowedScopes] = useState<number[]>([]);
  const [accessInvite, setAccessInvite] = useState<AccessRequiresInvite | null>(
    null,
  );
  const showToast = useToastStore((state) => state.showToast);

  const suppliersMap = useMemo(() => {
    const map: Record<string, string> = {};
    suppliers.forEach((s) => {
      if (s.id) map[s.id] = s.name || "";
    });
    return map;
  }, [suppliers]);

  const formatDateRu = (date: Date) => {
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = String(date.getFullYear());
    return `${dd}.${mm}.${yyyy}`;
  };

  useEffect(() => {
    if (isOpen) {
      loadAllowedScopes();
    }
  }, [isOpen]);

  const resolvedSupplierName = useMemo(() => {
    return decodedToken?.supplierId
      ? suppliersMap[decodedToken.supplierId] || "Поставщик"
      : "Поставщик";
  }, [decodedToken, suppliersMap]);

  const loadAllowedScopes = async () => {
    try {
      const tokenApi = new ApiToken();
      const response = await tokenApi.getTokenScopes();
      const requiredScopes = response.data.data
        .filter((scope) => scope.type === "REQUIRED")
        .map((scope) => {
          if (typeof scope.value === "number") return scope.value;
          const scopeMap: { [key: string]: number } = {
            content: 1,
            contentanalytics: 2,
            discountsandprices: 3,
            marketplace: 4,
            statistics: 5,
            advert: 6,
            questionsandfeedback: 7,
            recommendations: 8,
            buyerchat: 9,
            supplies: 10,
            returns: 11,
            // Финансы: по ним собирается фактический финотчёт WB, а WB переводит
            // его на метод, который без этой категории не пускает (SAEX-2902).
            finance: 13,
            "read-only": 30,
          };
          return scopeMap[scope.value] || 0;
        })
        .filter((num) => num > 0);
      setAllowedScopes(requiredScopes);
    } catch {
      setValidationError("Не удалось загрузить требования к токену");
    }
  };

  const permissionsList = useMemo<Permission[]>(
    () => [
      { name: "Аналитика", hasAccess: true },
      { name: "Вопросы и отзывы", hasAccess: true },
      { name: "Продвижение", hasAccess: true },
      { name: "Контент", hasAccess: true },
      { name: "Статистика", hasAccess: true },
      { name: "Маркетплейс", hasAccess: true },
      { name: "Финансы", hasAccess: true },
    ],
    [],
  );

  const tokenInfo = useMemo<TokenInfo | null>(() => {
    if (!decodedToken) return null;

    const resolvedName = decodedToken.supplierId
      ? suppliersMap[decodedToken.supplierId] || "Поставщик"
      : "Поставщик";

    const info: TokenInfo = {
      token: token.trim(),
      personId: decodedToken.supplierId || "unknown",
      personName: resolvedName,
      phone: "",
      status: decodedToken.expiredAt
        ? `Токен до ${formatDateRu(decodedToken.expiredAt)}`
        : "Токен",
      balance: "-",
      autoRenewal: true,
      permissions: {
        analytics: decodedToken.scopes.includes(2),
        reviews: decodedToken.scopes.includes(7),
        promotion: decodedToken.scopes.includes(6),
        content: decodedToken.scopes.includes(1),
        statistics: decodedToken.scopes.includes(5),
        marketplace: decodedToken.scopes.includes(4),
        finance: decodedToken.scopes.includes(13),
      },
    };

    return info;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decodedToken, suppliersMap]);

  const handleTokenChange = (value: string) => {
    setToken(value);
    setValidationError("");
    setDecodedToken(null);
    setAccessInvite(null);
  };

  const handleTokenSearch = () => {
    const value = token;
    if (value.trim() && allowedScopes.length > 0) {
      try {
        const decoded = decodeWbToken(value.trim(), allowedScopes);
        setDecodedToken(decoded);

        if (decoded.expired) setValidationError("Токен просрочен");
        else if (!decoded.isValidForOurUse)
          setValidationError("Токен не прошел валидацию");
        else {
          setValidationError("");
        }
      } catch {
        setValidationError("Неверный формат токена");
        setDecodedToken(null);
      }
    }
  };

  const handleTokenSubmit = async () => {
    if (!token.trim()) return setValidationError("Введите токен");
    if (!decodedToken?.isValidForOurUse)
      return setValidationError("Токен не прошел валидацию");

    if (tokenInfo) {
      const allPermissions = Object.values(tokenInfo.permissions);
      if (allPermissions.some((p) => !p)) {
        setValidationError("Не все права токена доступны");
        return;
      }
    }

    setIsConfirming(true);
    setValidationError("");
    setAccessInvite(null);

    try {
      const tokenApi = new ApiToken();
      const response = await tokenApi.addToken(token.trim());
      if (response.status === 201 && response.data?.supplier_id) {
        // Свежий токен уже подключённого кабинета сразу пускает в команду
        // (SAEX-3262) — без тоста человек не поймёт, что получил не владение.
        if (response.data.granted_role) {
          showToast(grantedRoleMessage(response.data.granted_role), "success", 6000);
        }
        refetchSuppliers();
        if (onTokenAdded) {
          onTokenAdded(response.data.supplier_id);
        }
        handleClose();
      } else setValidationError("Не удалось подтвердить токен");
    } catch (e: unknown) {
      const error = e as AxiosError<ApiErrorResponse>;
      const status = error.response?.status;
      const invite = readAccessRequiresInvite(status, error.response?.data);
      if (invite) {
        setAccessInvite(invite);
      } else {
        setValidationError(
          tokenAddErrorMessage(status, error.response?.data?.error?.code),
        );
      }
    } finally {
      setIsConfirming(false);
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setToken("");
      setShowToken(false);
      setDecodedToken(null);
      setValidationError("");
      setAccessInvite(null);
      setIsClosing(false);
      onClose();
    }, 300);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && decodedToken?.isValidForOurUse)
      handleTokenSubmit();
  };

  const handleInstructionClick = () => {
    setInstructionOpen((prev) => !prev);
  };

  if (!isOpen) return null;

  return (
    <div
      className={`${styles.overlay} ${isClosing ? styles.closing : ""}`}
      onClick={handleClose}
    >
      <div
        className={`${styles.modal} ${isClosing ? styles.closing : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.header}>
          <h2 className={styles.title}>Анализ токена доступа</h2>
          <button className={styles.closeButton} onClick={handleClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <div className={styles.content}>
          <div className={styles.inputSection}>
            <div className={styles.inputWrapper}>
              <div
                className={`${styles.tokenInputWrapper} ${
                  validationError ? styles.tokenInputError : ""
                }`}
              >
                <div className={styles.inputLabel}>
                  Токен{validationError && <span>: {validationError}</span>}
                </div>
                <input
                  type={showToken ? "text" : "password"}
                  className={styles.input}
                  value={token}
                  onChange={(e) => handleTokenChange(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Вставьте ваш токен здесь..."
                  disabled={isConfirming}
                />
                {token && (
                  <button
                    type="button"
                    className={styles.clearButton}
                    onClick={() => handleTokenChange("")}
                    disabled={isConfirming}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M18 6L6 18M6 6L18 18"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                )}
              </div>
              <button
                className={`${styles.submitButton} ${
                  tokenInfo ? styles.connectButton : styles.analyzeButton
                }`}
                onClick={tokenInfo ? handleTokenSubmit : handleTokenSearch}
                disabled={
                  !token.trim() ||
                  (tokenInfo
                    ? isConfirming ||
                      !decodedToken?.isValidForOurUse ||
                      Object.values(tokenInfo.permissions).some((p) => !p)
                    : false)
                }
              >
                {isConfirming ? (
                  <div className={styles.spinner}></div>
                ) : tokenInfo ? (
                  "Подтвердить"
                ) : (
                  "Найти"
                )}
              </button>
            </div>
          </div>
          <div
            className={`${styles.instructionToggleBtn} ${isInstructionOpen ? styles.instructionOpened : ""}`}
            onClick={handleInstructionClick}
          >
            <div>Инструкция</div>
            <DropdownIcon />
          </div>
          {isInstructionOpen && (
            <div className={styles.instructionContent}>
              <ol className={styles.instructionList}>
                <li>
                  Перейдите в раздел ЛК WB{" "}
                  <a
                    href="https://seller.wildberries.ru/api-integrations"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    “Интеграции по API”
                  </a>
                </li>
                <li>Нажмите кнопку “Создать токен”</li>
                <li>“Для интеграции вручную” &gt; “Базовый токен”</li>
                <li>Укажите название токена</li>
                <li>Выберите категории как на скриншоте ниже</li>
                <li>
                  Создайте токен, скопируйте его, и вставьте в поле под
                  инструкцией
                </li>
              </ol>

              <img src={instructionImg} />
            </div>
          )}

          {accessInvite ? (
            <div className={styles.accessInvite} role="status">
              <h3 className={styles.accessInviteTitle}>Кабинет уже подключён</h3>
              <p className={styles.accessInviteText}>
                {accessInvite.supplierName
                  ? `Кабинет «${accessInvite.supplierName}» уже подключён в SellerAlert.`
                  : "Этот кабинет уже подключён в SellerAlert."}{" "}
                Доступ выдаёт владелец — запросите его на сайте: войдите через
                Telegram, вставьте этот же токен и нажмите «Запросить доступ».
                Тогда после одобрения кабинет появится и здесь.
              </p>
              <button
                type="button"
                className={styles.accessInviteButton}
                onClick={() => openExternalLink(ADD_CABINET_WEB_URL)}
              >
                Запросить доступ на сайте
              </button>
              <p className={styles.accessInviteHint}>
                Подключали этот кабинет сами, но с другого аккаунта? Войдите на
                сайте тем способом, которым подключали кабинет, или объедините
                аккаунты в разделе «Мои данные».
              </p>
            </div>
          ) : (
            <div className={styles.permissionsSection}>
              <h3 className={styles.permissionsTitle}>
                {decodedToken ? `${resolvedSupplierName}:` : "Права доступа:"}
              </h3>
              <div className={styles.permissionsList}>
                {permissionsList.map((permission, index) => {
                  let hasAccess = false;
                  if (tokenInfo) {
                    switch (permission.name) {
                      case "Аналитика":
                        hasAccess = tokenInfo.permissions.analytics;
                        break;
                      case "Вопросы и отзывы":
                        hasAccess = tokenInfo.permissions.reviews;
                        break;
                      case "Продвижение":
                        hasAccess = tokenInfo.permissions.promotion;
                        break;
                      case "Контент":
                        hasAccess = tokenInfo.permissions.content;
                        break;
                      case "Статистика":
                        hasAccess = tokenInfo.permissions.statistics;
                        break;
                      case "Маркетплейс":
                        hasAccess = tokenInfo.permissions.marketplace;
                        break;
                      case "Финансы":
                        hasAccess = tokenInfo.permissions.finance;
                        break;
                      default:
                        hasAccess = false;
                    }
                  }

                  return (
                    <div
                      key={permission.name}
                      className={`${styles.permissionItem} ${
                        index === 0 ? styles.mainPermission : ""
                      }`}
                    >
                      <div
                        className={`${styles.permissionDot} ${
                          tokenInfo
                            ? hasAccess
                              ? styles.hasAccess
                              : styles.noAccess
                            : styles.inactive
                        }`}
                      >
                        {tokenInfo && hasAccess ? (
                          <svg
                            width="16"
                            height="11"
                            viewBox="0 0 16 13"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M16.75 0.75L5.75 11.75L0.75 6.75"
                              stroke="white"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        ) : tokenInfo ? (
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 14 14"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M12.75 0.75L0.75 12.75M0.75 0.75L12.75 12.75"
                              stroke="white"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        ) : (
                          <span>?</span>
                        )}
                      </div>
                      <span
                        className={`${styles.permissionName} ${
                          tokenInfo
                            ? hasAccess
                              ? styles.hasAccessText
                              : styles.noAccessText
                            : styles.inactiveText
                        }`}
                      >
                        {permission.name}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
